import { useState, useEffect } from 'react';
import { X, MessageCircle, Loader, Phone, ChevronDown } from 'lucide-react';

interface Country {
  code: string;
  name: string;
  flag: string;
}

const countries: Country[] = [
  { code: '55', name: 'Brasil', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '1', name: 'Estados Unidos', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '54', name: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}' },
  { code: '56', name: 'Chile', flag: '\u{1F1E8}\u{1F1F1}' },
  { code: '57', name: 'Colombia', flag: '\u{1F1E8}\u{1F1F4}' },
  { code: '52', name: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '51', name: 'Peru', flag: '\u{1F1F5}\u{1F1EA}' },
  { code: '598', name: 'Uruguai', flag: '\u{1F1FA}\u{1F1FE}' },
  { code: '595', name: 'Paraguai', flag: '\u{1F1F5}\u{1F1FE}' },
  { code: '591', name: 'Bolivia', flag: '\u{1F1E7}\u{1F1F4}' },
  { code: '34', name: 'Espanha', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: '351', name: 'Portugal', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: '49', name: 'Alemanha', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '33', name: 'Franca', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '39', name: 'Italia', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: '44', name: 'Reino Unido', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '86', name: 'China', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '81', name: 'Japao', flag: '\u{1F1EF}\u{1F1F5}' },
];

interface WhatsAppContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { phoneNumber: string }) => void;
  isSubmitting?: boolean;
}

export default function WhatsAppContactModal({
  isOpen,
  onClose,
  onSave,
  isSubmitting = false,
}: WhatsAppContactModalProps) {
  const [countryCode, setCountryCode] = useState('55');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCountryCode('55');
      setPhoneNumber('');
      setError(null);
      setIsCountryDropdownOpen(false);
    }
  }, [isOpen]);

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');

    if (countryCode === '55') {
      if (digits.length <= 2) {
        return digits;
      } else if (digits.length <= 7) {
        return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      } else {
        return `${digits.slice(0, 2)} ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
      }
    }

    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    const maxLength = countryCode === '55' ? 11 : 10;
    const limitedDigits = digits.slice(0, maxLength);
    setPhoneNumber(limitedDigits);
  };

  const validate = () => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (!digits) {
      setError('Numero de WhatsApp e obrigatorio');
      return false;
    } else if (digits.length < 8) {
      setError('Numero de WhatsApp invalido');
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const digits = phoneNumber.replace(/\D/g, '');
      const fullNumber = `${countryCode}${digits}`;
      onSave({ phoneNumber: fullNumber });
    }
  };

  const selectedCountry = countries.find((c) => c.code === countryCode) || countries[0];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Novo Numero
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numero do WhatsApp
            </label>
            <div className="flex gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                  disabled={isSubmitting}
                  className={`flex items-center gap-2 px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors min-w-[110px] ${
                    error ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <span className="text-lg leading-none">{selectedCountry.flag}</span>
                  <span className="text-sm font-medium text-gray-700">+{selectedCountry.code}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {isCountryDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                    {countries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => {
                          setCountryCode(country.code);
                          setIsCountryDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                          country.code === countryCode ? 'bg-green-50 text-green-700' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-lg leading-none">{country.flag}</span>
                        <span className="text-sm font-medium w-12">+{country.code}</span>
                        <span className="text-sm">{country.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={formatPhoneNumber(phoneNumber)}
                  onChange={handlePhoneChange}
                  placeholder={countryCode === '55' ? '11 99999-9999' : '999 999-9999'}
                  disabled={isSubmitting}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 transition-colors ${
                    error ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
              </div>
            </div>
            {error && (
              <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Numero completo: +{countryCode} {formatPhoneNumber(phoneNumber) || '...'}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Salvando...</span>
                </>
              ) : (
                <span>Adicionar</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
